import React from 'react';
import LogoDesk from '../images/LOGO-05.png';
import LogoMobile from '../images/LOGO-07.png';
import { useLocale } from '../contexts/LocaleContext';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
  const { t, setLocale, locale } = useLocale();

  return (
    <>
    <footer className="bg-primary-dark text-white py-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          {/* Logo and Company Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center mb-6">
              <picture>
                <source srcSet={LogoDesk} media="(min-width:1024px)" />
                <img src={LogoMobile} alt="Aizora" className="h-12 w-auto" />
              </picture>
            </div>
            <p className="text-gray-300 mb-6 max-w-md">
              {t.footer?.companyBlurb}
            </p>
            <div className="flex space-x-4">
              <a href={`mailto:${t.contact?.email || 'info@aizora.io'}`} className="inline-flex items-center justify-center w-9 h-9 rounded bg-white/10 text-white hover:bg-white/20" aria-label="Email">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </a>
              <a href="https://api.whatsapp.com/send?phone=543816461191" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center w-9 h-9 rounded bg-white/10 text-white hover:bg-white/20" aria-label="WhatsApp">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488"/>
                </svg>
              </a>
              {/* LinkedIn removed by request */}
            </div>
          </div>
          
          {/* Navigation Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t.footer?.navHeading}</h3>
            <ul className="space-y-2">
              {(t.footer?.nav || []).map((label: string, idx: number) => (
                <li key={idx}>
                  <a href={idx === 0 ? '/#hero' : idx === 1 ? '/#what' : idx === 2 ? '/#problem' : idx === 3 ? '/#contact' : '/#faq'} className="text-gray-300 hover:text-white transition-colors duration-300">
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t.footer?.contactHeading}</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <a href={`mailto:${t.contact?.email || 'contact@aizora.io'}`} className="text-gray-300 hover:text-white">{t.contact?.email || 'contact@aizora.io'}</a>
              </div>
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488"/>
                </svg>
                <span className="text-gray-300">+54 (381) 6461191</span>
              </div>
              {/* LinkedIn removed from contact list */}
            </div>
          </div>
        </div>
        
        {/* Bottom Section */}
        <div className="border-t border-gray-600 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-300 text-sm">
              {t.footer?.copyright}
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <div className="flex space-x-6">
                <Link to="/privacy-policy" className="text-gray-300 hover:text-white transition-colors duration-300">{t.footer?.privacy}</Link>
                <Link to="/terms-of-service" className="text-gray-300 hover:text-white transition-colors duration-300">{t.footer?.terms}</Link>
              </div>

              {/* Language selector as text links */}
              <div className="flex items-center gap-3">
                <span className="text-gray-400 text-sm mr-2">{t.footer?.languageLabel}</span>
                <button onClick={() => setLocale('en')} className={`text-gray-300 hover:text-white transition-colors ${locale === 'en' ? 'font-semibold' : ''}`}>{t.footer?.languageOptions?.[0] || 'EN'}</button>
                <span className="text-gray-400">|</span>
                <button onClick={() => setLocale('es')} className={`text-gray-300 hover:text-white transition-colors ${locale === 'es' ? 'font-semibold' : ''}`}>{t.footer?.languageOptions?.[1] || 'ES'}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
